import { or } from 'tio-ui/utilities';
import Switch from 'tio-ui/components/switch';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import FieldOptionsEditDialog from '../field-options/edit-dialog.gts';
import Input from 'tio-ui/components/input';
import Button from 'tio-ui/components/button';
import { on } from '@ember/modifier';
import type { GenericTemplateField } from '../../../types/tuition-assistance.ts';
import type { TemplateFieldName } from '../../../utils/tuition-assistance/fields.ts';
import type Owner from '@ember/owner';
import type { TasFieldOption as TasFieldOptionModel } from 'tio-data/data/resources/tas-field-option.type';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

export interface TuitionAssistanceProgramTemplateReviewToggleRowSignature {
  Args: {
    description: string;
    isDisabled?: boolean;
    isDisabledVisible?: boolean;
    isDisabledRequired?: boolean;
    isDisabledPicklist?: boolean;
    model: GenericTemplateField;
    didUpdateFieldConfig: (field: GenericTemplateField) => void;
    programTemplate?: TasProgramTemplateModel;
    fieldName?: TemplateFieldName;
    fieldOptions?: TasFieldOptionModel[];
  };
}

export default class TuitionAssistanceProgramTemplateReviewToggleRowComponent extends Component<TuitionAssistanceProgramTemplateReviewToggleRowSignature> {
  @tracked fieldCopy: GenericTemplateField = { ...this.args.model };

  constructor(
    owner: Owner,
    args: TuitionAssistanceProgramTemplateReviewToggleRowSignature['Args']
  ) {
    super(owner, args);
    if (this.args.model === undefined) {
      this.fieldCopy = {
        label: '',
        values: [],
        visible: false,
        province: 'APPLICATION',
        required: false,
        value_as_field_option: false,
      };
    }
  }

  @tracked isShowingOptionsDialog = false;

  isTrueOrFalse(value: boolean | undefined) {
    return value ? 'Yes' : 'No';
  }

  toggleOptionsDialog = () => {
    this.isShowingOptionsDialog = !this.isShowingOptionsDialog;
  };

  toggleVisibility = (value: boolean) => {
    const updated = { ...this.fieldCopy };
    updated.visible = value;
    if (value === false) {
      updated.required = false;
    }
    this.fieldCopy = updated;
    this.args.didUpdateFieldConfig(updated);
  };

  toggleRequired = (value: boolean) => {
    const updated = { ...this.fieldCopy };
    updated.required = value;
    if (value === true) {
      updated.visible = true;
    }
    this.fieldCopy = updated;
    this.args.didUpdateFieldConfig(updated);
  };

  togglePicklist = (value: boolean) => {
    const updated = { ...this.fieldCopy };
    updated.value_as_field_option = value;
    this.fieldCopy = updated;
    this.args.didUpdateFieldConfig(updated);
  };

  updateLabel = (label: string) => {
    const updated = { ...this.fieldCopy };
    updated.label = label;
    this.fieldCopy = updated;
    this.args.didUpdateFieldConfig(updated);
  };

  <template>
    <Input
      @value={{this.fieldCopy.label}}
      @onChange={{this.updateLabel}}
      class="col-span-1 text-sm"
    />
    <div class="col-span-1 self-center">
      <div class="tio-flex-tight">
        <label>{{t "common.no"}}</label>
        <Switch
          @label={{t "common.yes"}}
          @checked={{this.fieldCopy.visible}}
          @onChange={{this.toggleVisibility}}
          @isDisabled={{or @isDisabled @isDisabledVisible}}
        />
      </div>
    </div>
    <div class="col-span-1 self-center">
      <div class="tio-flex-tight">
        <label>{{t "common.no"}}</label>
        <Switch
          @label={{t "common.yes"}}
          @checked={{this.fieldCopy.required}}
          @onChange={{this.toggleRequired}}
          @isDisabled={{or @isDisabled @isDisabledRequired}}
        />
      </div>
    </div>
    <div class="col-span-1 self-center">
      <div class="tio-flex-tight">
        <label>{{t "common.no"}}</label>
        <Switch
          @label={{t "common.yes"}}
          @checked={{this.fieldCopy.value_as_field_option}}
          @onChange={{this.togglePicklist}}
          @isDisabled={{or @isDisabled @isDisabledPicklist}}
        />
      </div>
      {{#if this.fieldCopy.value_as_field_option}}
        <Button
          @appearance="minimal"
          @intent="primary"
          {{on "click" this.toggleOptionsDialog}}
          class="text-sm mx-1"
        >
          {{if
            @fieldOptions.length
            (t "common.tuition_assistance.program_template_review.edit_options")
            (t "common.tuition_assistance.program_template_review.add_options")
          }}
        </Button>
      {{/if}}
    </div>
    <div class="col-span-1 self-center">
      {{@description}}
    </div>

    {{#if this.isShowingOptionsDialog}}
      <FieldOptionsEditDialog
        {{! @glint-expect-error: not sure }}
        @programTemplate={{@programTemplate}}
        {{! @glint-expect-error: not sure }}
        @fieldName={{@fieldName}}
        @fieldLabel={{this.fieldCopy.label}}
        {{! @glint-expect-error: not sure }}
        @fieldOptions={{@fieldOptions}}
        @didCancel={{this.toggleOptionsDialog}}
        @didSubmit={{this.toggleOptionsDialog}}
      />
    {{/if}}
  </template>
}
